/**
 * Home Projects Background Preview Controller
 * Handles smooth crossfades of full-viewport project images on item hover/focus,
 * automatic rotation every 2.5s, and scroll/touch navigation (scroll down/up).
 */

export function initHomeProjects() {
   const home = document.querySelector('.js-home');
   if (!home) return;

   const bgItems = home.querySelectorAll('.js-home-bg-item');
   const projectItems = home.querySelectorAll('.js-home-project-item');

   if (!bgItems.length || !projectItems.length) return;

   const itemsArray = Array.from(projectItems);
   const defaultProjectId = home.dataset.defaultProject || (itemsArray[0] ? itemsArray[0].dataset.projectId : null);
   let currentActiveId = defaultProjectId;

   let currentIndex = itemsArray.findIndex(item => item.dataset.projectId === currentActiveId);
   if (currentIndex === -1) currentIndex = 0;

   // ------------------------------------------------------------------------
   // 1. Core State Switcher (Background Preview & Strikethrough)
   // ------------------------------------------------------------------------
   function setActiveProject(projectId) {
      if (!projectId) return;

      const newIndex = itemsArray.findIndex(item => item.dataset.projectId === projectId);
      if (newIndex !== -1) {
         currentIndex = newIndex;
      }

      if (projectId === currentActiveId && home.querySelector('.js-home-bg-item.is-active')) {
         return;
      }

      const targetBg = home.querySelector(`.js-home-bg-item[data-project-id="${projectId}"]`);
      if (targetBg) {
         bgItems.forEach(item => {
            item.classList.remove('is-active');
            const vid = item.querySelector('video');
            if (vid) vid.pause();
         });
         targetBg.classList.add('is-active');
         const targetVid = targetBg.querySelector('video');
         if (targetVid) {
            targetVid.currentTime = 0;
            targetVid.play().catch(() => {});
         }
         currentActiveId = projectId;
      }

      itemsArray.forEach(item => {
         if (item.dataset.projectId === projectId) {
            item.classList.add('is-current');
         } else {
            item.classList.remove('is-current');
         }
      });
   }

   function goToIndex(index) {
      if (itemsArray.length === 0) return;
      let targetIndex = index;
      if (targetIndex >= itemsArray.length) {
         targetIndex = 0;
      } else if (targetIndex < 0) {
         targetIndex = itemsArray.length - 1;
      }
      currentIndex = targetIndex;
      const targetItem = itemsArray[currentIndex];
      if (targetItem && targetItem.dataset.projectId) {
         setActiveProject(targetItem.dataset.projectId);
      }
   }

   function nextProject() {
      goToIndex(currentIndex + 1);
   }

   function prevProject() {
      goToIndex(currentIndex - 1);
   }

   // Initialize default active project video if applicable
   if (currentActiveId) {
      const activeBg = home.querySelector(`.js-home-bg-item[data-project-id="${currentActiveId}"]`);
      if (activeBg) {
         const activeVid = activeBg.querySelector('video');
         if (activeVid) activeVid.play().catch(() => {});
      }
   }

   // ------------------------------------------------------------------------
   // 2. Automatic Rotation (Autoplay every 2.5s)
   // ------------------------------------------------------------------------
   const AUTOPLAY_INTERVAL = 2500;
   let autoplayTimer = null;

   function startAutoplay() {
      stopAutoplay();
      autoplayTimer = setInterval(() => {
         nextProject();
      }, AUTOPLAY_INTERVAL);
   }

   function stopAutoplay() {
      if (autoplayTimer) {
         clearInterval(autoplayTimer);
         autoplayTimer = null;
      }
   }

   function resetAutoplay() {
      startAutoplay();
   }

   startAutoplay();

   // Pause autoplay when browser tab is inactive, resume when active
   document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
         stopAutoplay();
      } else {
         startAutoplay();
      }
   });

   // ------------------------------------------------------------------------
   // 3. Hover & Focus Listeners
   // ------------------------------------------------------------------------
   itemsArray.forEach(item => {
      const projectId = item.dataset.projectId;
      const link = item.querySelector('.s-home__project-link');

      item.addEventListener('mouseenter', () => {
         setActiveProject(projectId);
         resetAutoplay();
      });

      item.addEventListener('mousemove', () => {
         if (currentActiveId !== projectId) {
            setActiveProject(projectId);
            resetAutoplay();
         }
      });

      if (link) {
         link.addEventListener('focus', () => {
            setActiveProject(projectId);
            resetAutoplay();
         });
      }
   });

   // ------------------------------------------------------------------------
   // 3b. Click on the background image opens the active project (mobile)
   // ------------------------------------------------------------------------
   const bgLayer = home.querySelector('.js-home-bg-layer');
   const mobileQuery = window.matchMedia('(max-width: 860px)'); // = media-breakpoint(md)

   if (bgLayer) {
      bgLayer.addEventListener('click', () => {
         if (!mobileQuery.matches) return;
         const activeBg = home.querySelector('.js-home-bg-item.is-active');
         if (activeBg && activeBg.dataset.projectUrl) {
            window.location.href = activeBg.dataset.projectUrl;
         }
      });
   }

   // ------------------------------------------------------------------------
   // 4. Scroll Down / Up Navigation (Wheel with Inertia Lock)
   // ------------------------------------------------------------------------
   // When the project list overflows (mobile), scrolling over it scrolls the list
   // instead of switching projects
   const isInScrollableList = (target) => {
      const list = target instanceof Element ? target.closest('.js-home-projects-scroll') : null;
      return !!list && list.scrollHeight > list.clientHeight + 1;
   };

   let isScrollLocked = false;
   let scrollLockTimeout = null;

   window.addEventListener('wheel', (e) => {
      if (!document.body.contains(home)) return;

      if (isInScrollableList(e.target)) return;

      // Threshold to ignore micro-scroll jitter
      if (Math.abs(e.deltaY) < 15) return;

      e.preventDefault();

      if (isScrollLocked) return;

      isScrollLocked = true;
      clearTimeout(scrollLockTimeout);
      scrollLockTimeout = setTimeout(() => {
         isScrollLocked = false;
      }, 350);

      if (e.deltaY > 0) {
         // Scroll down -> next project
         nextProject();
      } else {
         // Scroll up -> previous project
         prevProject();
      }

      resetAutoplay();
   }, { passive: false });

   // ------------------------------------------------------------------------
   // 5. Touch Navigation for Mobile & Tablet (Swipe Up / Down)
   // ------------------------------------------------------------------------
   let touchStartY = 0;
   let touchStartX = 0;
   let isTouchLocked = false;

   window.addEventListener('touchstart', (e) => {
      if (!document.body.contains(home)) return;
      if (e.touches && e.touches.length === 1) {
         touchStartY = e.touches[0].clientY;
         touchStartX = e.touches[0].clientX;
         isTouchLocked = isInScrollableList(e.target);
      }
   }, { passive: true });

   window.addEventListener('touchmove', (e) => {
      if (!document.body.contains(home) || isTouchLocked || !e.touches || e.touches.length !== 1) return;

      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const diffY = touchStartY - currentY;
      const diffX = touchStartX - currentX;

      // Confirm vertical gesture intent (> 30px and more vertical than horizontal)
      if (Math.abs(diffY) > 30 && Math.abs(diffY) > Math.abs(diffX) * 1.5) {
         isTouchLocked = true;
         if (diffY > 0) {
            // Swipe up = natural scroll down -> next project
            nextProject();
         } else {
            // Swipe down = natural scroll up -> previous project
            prevProject();
         }
         resetAutoplay();
      }
   }, { passive: true });

   // ------------------------------------------------------------------------
   // 6. Keyboard Navigation (Arrows & Page Up/Down)
   // ------------------------------------------------------------------------
   window.addEventListener('keydown', (e) => {
      if (!document.body.contains(home)) return;

      if (['ArrowDown', 'PageDown'].includes(e.key)) {
         e.preventDefault();
         nextProject();
         resetAutoplay();
      } else if (['ArrowUp', 'PageUp'].includes(e.key)) {
         e.preventDefault();
         prevProject();
         resetAutoplay();
      }
   });
}
